import { FC, ReactNode, useEffect, useState } from 'react';
import { Modal, Loader, Center, Alert } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { Invoice, Patient, ServiceRequest } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { normalizeErrorString } from '@medplum/core';
import { PaymentSection } from '../components/PaymentSection';
import { usePaymentTracker } from '../hooks/usePaymentTracker';
import { getLinkedOrderRef } from '../utils/payment-helpers';

interface InvoiceDetailModalProps {
  opened: boolean;
  onClose: () => void;
  invoice: Invoice | null;
  patient?: Patient | null;
}

export const InvoiceDetailModal: FC<InvoiceDetailModalProps> = ({
  opened,
  onClose,
  invoice,
  patient,
}) => {
  const medplum = useMedplum();
  const [serviceRequest, setServiceRequest] = useState<ServiceRequest | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    if (!opened || !invoice) {
      setServiceRequest(null);
      setLoadError(null);
      return () => {
        active = false;
      };
    }

    setLoadError(null);

    const orderRef = getLinkedOrderRef(invoice);
    if (!orderRef) {
      setServiceRequest(null);
      return () => {
        active = false;
      };
    }

    setLoading(true);
    medplum
      .readReference({ reference: orderRef } as any)
      .then((sr) => {
        if (active) {
          setServiceRequest(sr as ServiceRequest);
        }
      })
      .catch((err) => {
        if (active) {
          setLoadError(normalizeErrorString(err));
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [opened, invoice, medplum]);

  const paymentTracker = usePaymentTracker(
    serviceRequest || undefined,
    patient || undefined
  );

  if (!invoice) {
    return null;
  }

  let content: ReactNode;
  if (loading) {
    content = (
      <Center p="xl">
        <Loader />
      </Center>
    );
  } else if (loadError) {
    content = (
      <Alert color="red" icon={<IconAlertCircle size={16} />}>
        {loadError}
      </Alert>
    );
  } else {
    content = (
      <PaymentSection
        serviceRequest={serviceRequest || undefined}
        paymentTracker={paymentTracker}
      />
    );
  }

  return (
    <Modal opened={opened} onClose={onClose} size="xl" centered>
      {content}
    </Modal>
  );
};
